import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, ShieldCheck, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [status, setStatus] = useState('verifying'); // verifying | valid | invalid | done
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function verify() {
      if (!token) {
        setStatus('invalid');
        setError('Manglende token i linket.');
        return;
      }
      try {
        const { data } = await api.get('/auth/verify-reset-token', { params: { token } });
        if (cancelled) return;
        setEmail(data.email || '');
        setStatus('valid');
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.detail || 'Linket er ugyldigt eller udløbet.');
        setStatus('invalid');
      }
    }
    verify();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) {
      toast.error('Adgangskoden skal være mindst 6 tegn');
      return;
    }
    if (pwd !== pwd2) {
      toast.error('Adgangskoderne matcher ikke');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pwd });
      setStatus('done');
      toast.success('Adgangskode nulstillet');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Nulstilling mislykkedes';
      toast.error(detail);
      // If token was consumed/expired between verify and submit, hard-fail the flow
      if ((detail || '').toLowerCase().includes('udløbet') || (detail || '').toLowerCase().includes('brugt') || (detail || '').toLowerCase().includes('ugyldigt')) {
        setStatus('invalid');
        setError(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#09090B]">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-4 mb-10">
          <img src="/samtale247-logo.webp" alt="samtale24-7.dk" className="w-16 h-16 rounded-full shrink-0" />
          <div className="font-display text-2xl font-bold tracking-tight">samtale24-7.dk</div>
        </div>

        <div className="bg-[#18181B] border border-border rounded-xl p-6 sm:p-8" data-testid="reset-password-card">
          {status === 'verifying' && (
            <div className="text-muted-foreground" data-testid="reset-verifying">
              Kontrollerer link…
            </div>
          )}

          {status === 'invalid' && (
            <div className="space-y-4" data-testid="reset-invalid">
              <div className="flex items-center gap-3 text-red-400">
                <AlertCircle className="w-6 h-6" />
                <h1 className="font-display text-xl font-bold">Ugyldigt eller udløbet link</h1>
              </div>
              <p className="text-sm text-muted-foreground">
                {error || 'Linket er ikke længere gyldigt. Anmod om et nyt nulstillingslink fra login-siden.'}
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-volt text-black hover:bg-volt font-semibold"
                data-testid="reset-back-to-login-btn">
                Til login
              </Button>
            </div>
          )}

          {status === 'valid' && (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              <div>
                <h1 className="font-display text-2xl font-bold mb-1">Vælg ny adgangskode</h1>
                {email && (
                  <p className="text-sm text-muted-foreground">
                    For konto <span className="text-white font-medium">{email}</span>
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="new-pwd">Ny adgangskode</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="new-pwd"
                    type={showPwd ? 'text' : 'password'}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    className="bg-[#09090B] border-border pr-10 focus-visible:ring-volt"
                    minLength={6}
                    required
                    data-testid="reset-new-password-input" />
                  <button type="button" onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="new-pwd2">Bekræft adgangskode</Label>
                <Input
                  id="new-pwd2"
                  type={showPwd ? 'text' : 'password'}
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  className="bg-[#09090B] border-border mt-1.5 focus-visible:ring-volt"
                  minLength={6}
                  required
                  data-testid="reset-confirm-password-input" />
              </div>

              <p className="text-xs text-muted-foreground">
                Linket er kun gyldigt i 1 time og kan kun bruges én gang.
              </p>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-volt text-black hover:bg-volt font-semibold"
                data-testid="reset-confirm-btn">
                {submitting ? 'Gemmer…' : (<>Gem ny adgangskode <ArrowRight className="w-4 h-4 ml-2" /></>)}
              </Button>
            </form>
          )}

          {status === 'done' && (
            <div className="space-y-4" data-testid="reset-done">
              <div className="flex items-center gap-3 text-volt">
                <ShieldCheck className="w-6 h-6" />
                <h1 className="font-display text-xl font-bold">Adgangskode nulstillet</h1>
              </div>
              <p className="text-sm text-muted-foreground">
                Din adgangskode er blevet opdateret. Du kan nu logge ind med din nye adgangskode.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-volt text-black hover:bg-volt font-semibold"
                data-testid="reset-goto-login-btn">
                Log ind
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
